// frontend/shared-ui/src/components/InvoiceTemplate.jsx
import React from "react";

const T = {
  en: {
    customer: "Customer",
    address: "Address",
    date: "Date",
    invoiceNo: "Invoice No",
    offerNo: "Offer No",
    orderNo: "Order No",
    docTitle: { invoice: "INVOICE", offer: "OFFER", order: "ORDER" },
    price: "Unit Price",
    qty: "Qty",
    details: "Details",
    amount: "Amount",
    grandTotal: "GRAND TOTAL",
    errorsFooter: "Errors and omissions excepted · Both parties responsible",
    signature: "Signature",
  },
  ku: {
    customer: "حضرة السيد",
    address: "العنوان",
    date: "التاريخ",
    invoiceNo: "ز. فاتوورە",
    offerNo: "ز. پێشنیار",
    orderNo: "ز. داواکاری",
    docTitle: { invoice: "فاتوورە", offer: "پێشنیار", order: "داواکاری" },
    price: "سعر",
    qty: "العدد",
    details: "التفاصيل",
    amount: "المبلغ",
    grandTotal: "المبلغ الكلي",
    errorsFooter: "الغلط والسهو مرجوع للطرفين",
    signature: "التوقيع",
  },
};

const ROWS_PER_PAGE = 15; // Max rows per A4 page

function formatNumber(value, currency) {
  const n = Number(value) || 0;
  return `${n.toFixed(2)}${currency ? " " + currency : ""}`;
}

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return `${dt.getFullYear()} / ${String(dt.getMonth() + 1).padStart(2, "0")} / ${String(dt.getDate()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

export default function InvoiceTemplate({
  kind = "invoice",
  order = {},
  store = {},
  lang = "ku",
  logoUrl = "",
  showPrintButton = false,
  onPrint,
}) {
  const t = T[lang] || T.ku;
  const isKu = lang === "ku";
  const direction = isKu ? "rtl" : "ltr";

  const safeOrder = order || {};
  const safeStore = store || {};
  const items = Array.isArray(safeOrder.items) ? safeOrder.items : [];
  const currency = safeOrder.currency || safeStore.currency || "USD";

  const kindKey = ["invoice", "offer", "order"].includes(kind)
    ? kind
    : "invoice";
  const docTitle = t.docTitle[kindKey] || t.docTitle.invoice;
  const numLabel =
    kindKey === "offer"
      ? t.offerNo
      : kindKey === "order"
        ? t.orderNo
        : t.invoiceNo;

  // Chunk items into pages
  const pages = [];
  for (
    let i = 0;
    i < Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE));
    i++
  ) {
    pages.push(items.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE));
  }

  const renderHeader = (isFirstPage) => (
    <header
      className={`fh-invoice__header ${!isFirstPage ? "is-subsequent" : ""}`}
    >
      <div className="fh-invoice__header-desc">
        <div className="fh-invoice__header-title">
          {isKu
            ? "فرۆشتى كەلۆپەلى ئينتەرنێت و كێبڵى تیشكى"
            : "Internet & Fiber Optic Solutions"}
        </div>
        <div className="fh-invoice__header-subtitle">
          Internet & Fiber Optic Solutions
        </div>
        <div className="fh-invoice__contacts-grid">
          <div className="fh-invoice__contact-line">
            <span className="fh-invoice__contact-icon">📞</span>
            <span dir="ltr">{safeStore.phone || ""}</span>
          </div>
          <div className="fh-invoice__contact-line">
            <span className="fh-invoice__contact-icon">🌐</span>
            <span dir="ltr">{safeStore.website || ""}</span>
          </div>
          <div className="fh-invoice__contact-line">
            <span className="fh-invoice__contact-icon">📞</span>
            <span dir="ltr">{safeStore.altPhone || ""}</span>
          </div>
          <div className="fh-invoice__contact-line">
            <span className="fh-invoice__contact-icon">✉</span>
            <span dir="ltr">{safeStore.email || ""}</span>
          </div>
          <div className="fh-invoice__contact-line fh-invoice__contact-line--wide">
            <span className="fh-invoice__contact-icon">📍</span>
            <span>{safeStore.address || ""}</span>
          </div>
        </div>
      </div>
      <div className="fh-invoice__header-logo">
        {logoUrl && (
          <img src={logoUrl} alt="Logo" className="fh-invoice__logo" />
        )}
      </div>
    </header>
  );

  const renderMeta = () => (
    <div className="fh-invoice__meta-bar">
      <div className="fh-invoice__meta-item">
        <span className="fh-invoice__meta-label">{t.customer}:</span>
        <span className="fh-invoice__meta-value">
          {safeOrder.customerName || safeOrder.customer?.name || ""}
        </span>
      </div>
      <div className="fh-invoice__meta-item">
        <span className="fh-invoice__meta-label">{t.address}:</span>
        <span className="fh-invoice__meta-value">
          {safeOrder.customerAddress || ""}
        </span>
      </div>
      <div className="fh-invoice__meta-item fh-invoice__meta-item--date">
        <span className="fh-invoice__meta-label">{t.date}:</span>
        <span className="fh-invoice__meta-value">
          {fmtDate(safeOrder.issuedAt)}
        </span>
      </div>
    </div>
  );

  const renderTable = (pageItems, isFirstPage, isLastPage) => {
    const paddingCount = isLastPage
      ? Math.max(0, ROWS_PER_PAGE - pageItems.length)
      : 0;
    const paddingRows = Array.from({ length: paddingCount });

    return (
      <table className="fh-invoice__table">
        <thead>
          <tr>
            {isKu ? (
              <>
                <th className="fh-col-amount">{t.amount}</th>
                <th className="fh-col-details">{t.details}</th>
                <th className="fh-col-qty">{t.qty}</th>
                <th className="fh-col-price">{t.price}</th>
              </>
            ) : (
              <>
                <th className="fh-col-price">{t.price}</th>
                <th className="fh-col-qty">{t.qty}</th>
                <th className="fh-col-details">{t.details}</th>
                <th className="fh-col-amount">{t.amount}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {pageItems.map((it, idx) => (
            <tr key={it.id || idx}>
              {isKu ? (
                <>
                  <td className="fh-col-amount">
                    {formatNumber(it.lineTotal, currency)}
                  </td>
                  <td className="fh-col-details">
                    <div className="fh-invoice__item-name">
                      {it.productName || ""}
                    </div>
                  </td>
                  <td className="fh-col-qty" dir="ltr">
                    {`${it.quantity ?? ""} ${it.unit || ""}`.trim()}
                  </td>
                  <td className="fh-col-price">
                    {formatNumber(it.unitPrice, "")}
                  </td>
                </>
              ) : (
                <>
                  <td className="fh-col-price">
                    {formatNumber(it.unitPrice, "")}
                  </td>
                  <td className="fh-col-qty" dir="ltr">
                    {`${it.quantity ?? ""} ${it.unit || ""}`.trim()}
                  </td>
                  <td className="fh-col-details">
                    <div className="fh-invoice__item-name">
                      {it.productName || ""}
                    </div>
                  </td>
                  <td className="fh-col-amount">
                    {formatNumber(it.lineTotal, currency)}
                  </td>
                </>
              )}
            </tr>
          ))}
          {paddingRows.map((_, idx) => (
            <tr key={`pad-${idx}`} className="fh-invoice__row-empty">
              <td className="fh-col-amount">&nbsp;</td>
              <td className="fh-col-details">&nbsp;</td>
              <td className="fh-col-qty">&nbsp;</td>
              <td className="fh-col-price">&nbsp;</td>
            </tr>
          ))}
        </tbody>
        {isLastPage && (
          <tfoot>
            <tr>
              <td colSpan={4} className="fh-invoice__grand-row">
                <div className="fh-invoice__grand-inner">
                  <span className="fh-invoice__grand-label">
                    {t.grandTotal}
                  </span>
                  <span className="fh-invoice__grand-value" dir="ltr">
                    {formatNumber(safeOrder.totalAmount, currency)}
                  </span>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    );
  };

  const renderFooter = () => (
    <footer className="fh-invoice__footer">
      <div className="fh-invoice__footer-left">{t.signature}</div>
      <div className="fh-invoice__footer-right">{t.errorsFooter}</div>
    </footer>
  );

  return (
    <div className={`fh-invoice ${isKu ? "is-rtl" : "is-ltr"}`} dir={direction}>
      {showPrintButton && (
        <div className="fh-invoice__toolbar no-print">
          <button
            type="button"
            className="fh-btn fh-btn--primary"
            onClick={onPrint || (() => window.print())}
          >
            🖨 Print / Save as PDF
          </button>
        </div>
      )}

      {pages.map((pageItems, pageIdx) => {
        const isFirstPage = pageIdx === 0;
        const isLastPage = pageIdx === pages.length - 1;

        return (
          <div key={pageIdx} className="fh-invoice__sheet">
            {logoUrl && (
              <div className="fh-invoice__watermark">
                <img src={logoUrl} alt="" />
              </div>
            )}

            {renderHeader(isFirstPage)}
            {renderMeta()}

            <div className="fh-invoice__doc-id">
              <span className="fh-invoice__doc-id-label">{numLabel}:</span>
              <span className="fh-invoice__doc-id-value" dir="ltr">
                {safeOrder.code || ""}
              </span>
              <span className="fh-invoice__doc-id-type">— {docTitle}</span>
            </div>

            {renderTable(pageItems, isFirstPage, isLastPage)}
            {renderFooter()}
          </div>
        );
      })}
    </div>
  );
}
